export type CohortAssignable = {
  cohort_ids?: number[];
  cohorts?: Array<{ cohort_id: number }>;
};

export type DashboardScenario = {
  id: number;
  name: string;
  description: string;
};

type LearningPathLike = CohortAssignable & {
  tag?: string;
  prompts?: Array<{
    prompt: {
      id: number;
      name: string;
      system_prompt: string;
      is_active?: boolean;
    };
  }>;
};

export function getItemCohortIds(item: CohortAssignable): number[] {
  if (item.cohorts?.length) {
    return item.cohorts
      .map((cohort) => cohort.cohort_id)
      .filter((cohortId): cohortId is number => typeof cohortId === 'number');
  }

  return item.cohort_ids ?? [];
}

export function isAllCohortsSelected(
  selectedCohortIds: number[],
  allCohortIds: number[]
): boolean {
  if (allCohortIds.length === 0) {
    return true;
  }

  if (selectedCohortIds.length === 0) {
    return false;
  }

  return allCohortIds.every((id) => selectedCohortIds.includes(id));
}

export function filterItemsByCohortIds<T extends CohortAssignable>(
  items: T[],
  selectedCohortIds: number[],
  allCohortIds: number[] = []
): T[] {
  if (isAllCohortsSelected(selectedCohortIds, allCohortIds)) {
    return items;
  }

  return items.filter((item) =>
    getItemCohortIds(item).some((cohortId) =>
      selectedCohortIds.includes(cohortId)
    )
  );
}

type LearningPathPromptEntry = NonNullable<LearningPathLike['prompts']>[number];

export function getUniqueLearningPathPrompts<T>(
  learningPaths: LearningPathLike[],
  selectedCohortIds: number[],
  allCohortIds: number[] = [],
  mapPrompt: (
    entry: LearningPathPromptEntry,
    path: LearningPathLike
  ) => T
): T[] {
  const filteredPaths = filterItemsByCohortIds(
    learningPaths,
    selectedCohortIds,
    allCohortIds
  );

  const seenPromptIds = new Set<number>();
  const results: T[] = [];

  for (const path of filteredPaths) {
    for (const entry of path.prompts || []) {
      if (seenPromptIds.has(entry.prompt.id)) {
        continue;
      }

      seenPromptIds.add(entry.prompt.id);
      results.push(mapPrompt(entry, path));
    }
  }

  return results;
}

export function learningPathsToScenarios(
  learningPaths: LearningPathLike[],
  selectedCohortIds: number[],
  allCohortIds: number[] = []
): DashboardScenario[] {
  return getUniqueLearningPathPrompts(
    learningPaths,
    selectedCohortIds,
    allCohortIds,
    (entry) => ({
      id: entry.prompt.id,
      name: entry.prompt.name,
      description: entry.prompt.system_prompt,
    })
  );
}
